import { Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import * as alertifyjs from 'alertifyjs';
import { DomiciliacionCtService, ModoInvitacion } from 'src/app/services/domiciliacion-ct/domiciliacion-ct.service';

/**
 * «Domiciliación con CrediTotal»: estado, verificación y los dos cambios (quién es invitado a aceptar
 * y qué empresa cobra los lotes nuevos de BDV y Mercantil). Forma del switch de pagos (perfil bancario).
 */
@Component({
  selector: 'app-domiciliacion-ct-card',
  templateUrl: './domiciliacion-ct-card.component.html',
  styleUrls: ['./domiciliacion-ct-card.component.css']
})
export class DomiciliacionCtCardComponent implements OnInit {

  estado: any = null;            // data de GET /api/domiciliacion-ct
  verificacion: any = null;      // data de GET /api/domiciliacion-ct/verificacion
  cargando = false;
  verificando = false;
  guardando = false;
  sinPermiso = false;
  error: string | null = null;
  errorVerificacion: string | null = null;
  modoElegido: ModoInvitacion = 'APAGADA';
  piloto: any = null;            // data de GET /api/domiciliacion-ct/piloto
  errorPiloto: string | null = null;
  cedulaNueva = '';

  constructor(private servicio: DomiciliacionCtService) {}

  ngOnInit(): void {
    this.cargar();
  }

  /** Estado y verificación. Un 403 en el estado oculta la sección; un fallo de la verificación no la tumba. */
  async cargar(): Promise<void> {
    this.cargando = true;
    this.error = null;
    try {
      const r = await this.servicio.getEstado();
      this.estado = r?.data || null;
      this.sinPermiso = false;
      if (this.estado) this.modoElegido = this.estado.invitacion;
    } catch (e: any) {
      this.estado = null;
      if (e?.status === 403) this.sinPermiso = true;
      else this.error = this.mensaje(e, 'No se pudo consultar la domiciliación con CrediTotal');
    } finally {
      this.cargando = false;
    }
    if (this.estado) await Promise.all([this.verificar(), this.cargarPiloto()]);
  }

  async cargarPiloto(): Promise<void> {
    this.errorPiloto = null;
    try {
      const r = await this.servicio.getPiloto();
      this.piloto = r?.data || null;
    } catch (e: any) {
      this.piloto = null;
      this.errorPiloto = this.mensaje(e, 'No se pudo consultar el grupo piloto');
    }
  }

  /** Motivo obligatorio, como en los demás cambios. Devuelve null si se cancela. */
  private async pedirMotivo(titulo: string, html: string, boton: string): Promise<string | null> {
    const r = await Swal.fire({
      title: titulo,
      html,
      icon: 'question',
      input: 'textarea',
      inputLabel: 'Motivo (queda en el historial)',
      inputValidator: (v: string) => (!v || !v.trim()) ? 'El motivo es obligatorio' : null,
      showCancelButton: true,
      cancelButtonColor: '#6c757d',
      confirmButtonText: boton,
      cancelButtonText: 'Cancelar'
    });
    return r.isConfirmed ? String(r.value).trim() : null;
  }

  cedulaValida(): boolean {
    return /^\d{5,10}$/.test(String(this.cedulaNueva || '').replace(/\D/g, '')) && !this.guardando;
  }

  async agregarAlPiloto(): Promise<void> {
    if (!this.cedulaValida()) return;
    const cedula = String(this.cedulaNueva).replace(/\D/g, '');
    const motivo = await this.pedirMotivo(`¿Agregar la cédula ${cedula} al grupo piloto?`,
      'Si la invitación está en «grupo piloto», esta persona verá la invitación a aceptar los términos de CrediTotal al abrir la aplicación.',
      'Sí, agregar');
    if (!motivo) return;
    this.guardando = true;
    try {
      await this.servicio.agregarAlPiloto(cedula, motivo);
      alertifyjs.success(`Cédula ${cedula} agregada al grupo piloto`);
      this.cedulaNueva = '';
    } catch (e: any) {
      alertifyjs.error(this.mensajeCambio(e));
    } finally {
      this.guardando = false;
    }
    await this.cargar();
  }

  async quitarDelPiloto(p: any): Promise<void> {
    if (this.guardando || !p?.code) return;
    const motivo = await this.pedirMotivo(`¿Quitar la cédula ${p.cedula || p.code} del grupo piloto?`,
      'Deja de ver la invitación. Si ya aceptó los términos de CrediTotal, conserva su aceptación.',
      'Sí, quitar');
    if (!motivo) return;
    this.guardando = true;
    try {
      await this.servicio.quitarDelPiloto(p.code, motivo);
      alertifyjs.success(`Cédula ${p.cedula || p.code} quitada del grupo piloto`);
    } catch (e: any) {
      alertifyjs.error(this.mensajeCambio(e));
    } finally {
      this.guardando = false;
    }
    await this.cargar();
  }

  async verificar(): Promise<void> {
    this.verificando = true;
    this.errorVerificacion = null;
    try {
      const r = await this.servicio.getVerificacion();
      this.verificacion = r?.data || null;
    } catch (e: any) {
      this.verificacion = null;
      this.errorVerificacion = this.mensaje(e, 'No se pudo verificar');
    } finally {
      this.verificando = false;
    }
  }

  private mensaje(e: any, porDefecto: string): string {
    return e?.error?.message || e?.error?.msg || porDefecto;
  }

  /** Mensaje de un POST fallido: el 403 es falta de 2FA o de permiso. */
  mensajeCambio(e: any): string {
    if (e?.status === 403) {
      const detalle = e?.error?.message ? ` (${e.error.message})` : '';
      return `No se hizo el cambio: hace falta tener activa la verificación en dos pasos (2FA) en su usuario, o no tiene permiso para cambiarlo${detalle}.`;
    }
    return this.mensaje(e, 'No se pudo hacer el cambio');
  }

  // ---- Textos para el gerente ----

  textoInvitacion(): string {
    const i = this.estado?.invitacion;
    if (i === 'TODOS') return 'Se invita a todos los clientes';
    if (i === 'PILOTO') return `Solo el grupo piloto (${this.estado?.piloto_total || 0} personas)`;
    return 'Apagada: no se invita a nadie';
  }

  claseInvitacion(): string {
    const i = this.estado?.invitacion;
    return i === 'TODOS' ? 'kb-badge-exito' : i === 'PILOTO' ? 'kb-badge-alerta' : 'kb-badge-neutro';
  }

  textoEmpresa(): string {
    return this.estado?.empresa === 'CT' ? 'CT · CrediTotal' : 'MT · Mundo Total';
  }

  claseEstadoRevision(estado: string): string {
    if (estado === 'OK') return 'kb-badge-exito';
    if (estado === 'FALLA') return 'kb-badge-peligro';
    return 'kb-badge-neutro';
  }

  textoEstadoRevision(estado: string): string {
    if (estado === 'OK') return 'Listo';
    if (estado === 'FALLA') return 'Falla';
    return 'No se pudo verificar';
  }

  textoModo(modo: string): string {
    if (modo === 'TODOS') return 'Todos los clientes';
    if (modo === 'PILOTO') return 'Solo el grupo piloto';
    if (modo === 'APAGADA') return 'Apagada';
    return modo || '—';
  }

  textoEmpresaCorta(e: string): string {
    return e === 'CT' ? 'CrediTotal' : e === 'MT' ? 'Mundo Total' : e || '—';
  }

  /** PILOTO solo se ofrece si hay personas en el piloto. */
  modosDisponibles(): ModoInvitacion[] {
    const modos: ModoInvitacion[] = ['APAGADA'];
    if ((this.estado?.piloto_total || 0) > 0) modos.push('PILOTO');
    modos.push('TODOS');
    return modos;
  }

  puedeCambiarInvitacion(): boolean {
    return !!this.estado && !this.guardando && this.modoElegido !== this.estado.invitacion
      && this.modosDisponibles().includes(this.modoElegido);
  }

  /** Pasar a CrediTotal exige la verificación aprobada. */
  puedePasarACt(): boolean {
    return !!this.estado && !this.guardando && this.estado.empresa !== 'CT' && this.verificacion?.resultado === 'APROBADA';
  }

  /** Volver a Mundo Total siempre se puede si hoy cobra CrediTotal. */
  puedeVolverAMt(): boolean {
    return !!this.estado && !this.guardando && this.estado.empresa === 'CT';
  }

  /** Datos de cobro de CrediTotal que faltan, por banco. */
  textoCobro(o: any): string {
    return o?.completo ? 'Completos' : `Faltan: ${(o?.faltan || []).join(', ') || 'datos'}`;
  }

  async cambiarInvitacion(): Promise<void> {
    if (!this.puedeCambiarInvitacion()) return;
    const modo = this.modoElegido;
    const quien = modo === 'TODOS' ? 'a <strong>todos los clientes</strong>' : modo === 'PILOTO'
      ? `solo al <strong>grupo piloto</strong> (${this.estado.piloto_total} personas)` : 'a <strong>nadie</strong>';
    const r = await Swal.fire({
      title: `¿Cambiar la invitación a «${this.textoModo(modo)}»?`,
      html: `Desde ahora la aplicación invita a aceptar los términos de la domiciliación con CrediTotal ${quien}. `
        + 'Quien ya aceptó conserva su aceptación. Esto no cambia qué empresa cobra.',
      icon: 'warning',
      input: 'textarea',
      inputLabel: 'Motivo (queda en el historial)',
      inputValidator: (v: string) => (!v || !v.trim()) ? 'El motivo es obligatorio' : null,
      showCancelButton: true,
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, cambiar la invitación',
      cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;
    this.guardando = true;
    try {
      await this.servicio.cambiarInvitacion(modo, String(r.value).trim());
      alertifyjs.success(`Invitación cambiada: ${this.textoModo(modo)}`);
    } catch (e: any) {
      alertifyjs.error(this.mensajeCambio(e));
    } finally {
      this.guardando = false;
    }
    await this.cargar();
  }

  async cambiarEmpresa(empresa: 'MT' | 'CT'): Promise<void> {
    if (empresa === 'CT' ? !this.puedePasarACt() : !this.puedeVolverAMt()) return;
    const aCt = empresa === 'CT';
    const r = await Swal.fire({
      title: aCt ? '¿Pasar la domiciliación a CrediTotal?' : '¿Volver la domiciliación a Mundo Total?',
      html: aCt
        ? 'Desde ese momento los lotes nuevos de BDV y Mercantil <strong>solo llevan a los clientes que aceptaron</strong> '
          + 'y salen con la <strong>cuenta de CrediTotal</strong>. Los lotes ya generados se concilian con la empresa con la que salieron.'
        : 'Los lotes nuevos de BDV y Mercantil <strong>salen como hoy</strong>, con la cuenta de Mundo Total. '
          + 'Los lotes ya generados se concilian con la empresa con la que salieron.',
      icon: 'warning',
      input: 'textarea',
      inputLabel: 'Motivo (queda en el historial)',
      inputValidator: (v: string) => (!v || !v.trim()) ? 'El motivo es obligatorio' : null,
      showCancelButton: true,
      cancelButtonColor: '#6c757d',
      confirmButtonText: aCt ? 'Sí, pasar a CrediTotal' : 'Sí, volver a Mundo Total',
      cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;
    this.guardando = true;
    try {
      await this.servicio.cambiarEmpresa(empresa, String(r.value).trim());
      alertifyjs.success(aCt ? 'La domiciliación ahora cobra con CrediTotal' : 'La domiciliación volvió a Mundo Total');
    } catch (e: any) {
      if (e?.status === 409 && e?.error?.verificacion) {
        this.verificacion = e.error.verificacion;
        alertifyjs.error(`No se cambió: ${this.mensaje(e, 'la verificación no está aprobada')}`);
      } else if (e?.status === 409) {
        alertifyjs.error(this.mensaje(e, 'No se cambió: el estado ya es otro'));
      } else {
        alertifyjs.error(this.mensajeCambio(e));
      }
    } finally {
      this.guardando = false;
    }
    await this.cargar();
  }
}
